import { useState, useEffect, useRef } from "react";
import LiquidEther from "./LiquidEther";

const NAV_LINKS = [
  { label: "Lavori", anchor: "lavori" },
  { label: "Servizi", anchor: "servizi" },
  { label: "Chi Siamo", anchor: "chi-siamo" },
  { label: "Contatti", anchor: "contatti" },
];

const SERVICES = [
  { num: "01", title: "Identità & Brand Design", desc: "Logo, palette, font, grafiche promozionali e materiali offline. Costruiamo o rinfreschiamo la tua identità visiva da zero, con un sistema coerente su ogni touchpoint." },
  { num: "02", title: "Advertising & Paid Ads", desc: "Campagne pubblicitarie su Google, Meta, TikTok e oltre. Gestiamo budget, creatività e ottimizzazione per portarti clienti reali, non solo click." },
  { num: "03", title: "Sito Web & SEO", desc: "Progettiamo o rimettiamo a nuovo il tuo sito, e lo posizioniamo sui motori di ricerca. Ogni pagina è pensata per convertire, non solo per essere bella." },
  { num: "04", title: "Contenuti & Produzione", desc: "Giriamo video, produciamo foto e scriviamo copy per i tuoi canali. Dai Reels alle storie di brand — contenuti che fermano lo scroll e raccontano chi sei." },
  { num: "05", title: "Social Media Management", desc: "Gestiamo i tuoi profili social dalla strategia alla pubblicazione. Presenza costante, tono di voce coerente e community che cresce nel tempo." },
  { num: "06", title: "Strategia & Consulenza", desc: "Prima di fare, pensiamo. Definiamo con te obiettivi, posizionamento e piano d'azione. Sei seguito passo dopo passo, con report chiari e comunicazione diretta." },
];

const STATS = [
  { value: "+30", label: "Clienti Soddisfatti" },
  { value: "9.1×", label: "ROI Medio" },
  { value: "5 anni", label: "Nel Settore" },
];

function scrollTo(anchor) {
  const el = document.getElementById(anchor);
  if (el) el.scrollIntoView({ behavior: "smooth" });
}

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function FadeUp({ children, delay = 0, className = "" }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0)" : "translateY(40px)",
      transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
    }}>
      {children}
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", message: "", privacy: false });
  const [status, setStatus] = useState(null);

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.message || !form.privacy) return;
    setStatus("sending");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) { setStatus("ok"); setForm({ name: "", email: "", message: "", privacy: false }); }
      else setStatus("error");
    } catch { setStatus("error"); }
  };

  const inputStyle = {
    width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 12, padding: "14px 18px", color: "#f0f0f0", fontSize: "0.9rem",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif",
    outline: "none", transition: "border-color 0.2s", boxSizing: "border-box",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <input placeholder="Nome" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle}
        onFocus={e => e.target.style.borderColor = "#7c3aed"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"} />
      <input placeholder="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle}
        onFocus={e => e.target.style.borderColor = "#7c3aed"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"} />
      <textarea placeholder="Raccontaci il tuo progetto..." value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
        rows={5} style={{ ...inputStyle, resize: "none", lineHeight: 1.7 }}
        onFocus={e => e.target.style.borderColor = "#7c3aed"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"} />

      {/* Privacy checkbox */}
      <label style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}>
        <input type="checkbox" checked={form.privacy} onChange={e => setForm({ ...form, privacy: e.target.checked })}
          style={{ marginTop: 3, accentColor: "#7c3aed", width: 16, height: 16, flexShrink: 0 }} />
        <span style={{ fontSize: "0.75rem", color: "#555", lineHeight: 1.6, fontFamily: "DM Sans, sans-serif" }}>
          Ho letto e accetto la{" "}
          <a href="#" style={{ color: "#a78bfa", textDecoration: "underline" }}>Privacy Policy</a>
          {" "}ai sensi del Regolamento UE 2016/679 (GDPR). I dati forniti saranno trattati esclusivamente per rispondere alla tua richiesta.
        </span>
      </label>

      <button className="btn-primary"
        style={{ width: "100%", justifyContent: "center", padding: "16px", fontSize: "0.88rem", opacity: (!form.privacy || status === "sending") ? 0.5 : 1 }}
        onClick={handleSubmit} disabled={!form.privacy || status === "sending"}>
        {status === "sending" ? "Invio in corso..." : "Invia Messaggio ↗"}
      </button>
      {status === "ok" && <p style={{ color: "#a78bfa", fontSize: "0.85rem", textAlign: "center" }}>✓ Messaggio inviato! Ti risponderemo presto.</p>}
      {status === "error" && <p style={{ color: "#f87171", fontSize: "0.85rem", textAlign: "center" }}>Errore nell'invio. Scrivici a info@mediastreetagency.it</p>}
    </div>
  );
}

export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [cursor, setCursor] = useState({ x: -100, y: -100 });
  const [hovered, setHovered] = useState(false);
  const isTouch = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const move = (e) => setCursor({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);

  return (
    <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif", background: "#050505", color: "#f0f0f0", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;1,9..40,300&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; -webkit-text-size-adjust: 100%; }
        ::selection { background: #7c3aed; color: #fff; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #0a0a0a; }
        ::-webkit-scrollbar-thumb { background: #7c3aed; border-radius: 2px; }
        .cursor-dot { width:10px; height:10px; background:#7c3aed; border-radius:50%; position:fixed; pointer-events:none; z-index:9999; transform:translate(-50%,-50%); transition:width 0.2s,height 0.2s; }
        @media (pointer: coarse) { .cursor-dot, .cursor-ring { display:none!important; } }
        .cursor-ring { width:36px; height:36px; border:1.5px solid rgba(124,58,237,0.5); border-radius:50%; position:fixed; pointer-events:none; z-index:9998; transform:translate(-50%,-50%); transition:width 0.3s,height 0.3s,border-color 0.3s; }
        .cursor-ring.hovered { width:64px; height:64px; border-color:#7c3aed; }
        .nav-link { color:#aaa; text-decoration:none; font-size:0.85rem; letter-spacing:0.08em; text-transform:uppercase; font-weight:500; transition:color 0.2s; background:none; border:none; cursor:pointer; font-family:inherit; padding:0; }
        .nav-link:hover { color:#f0f0f0; }
        .pill { display:inline-block; padding:6px 16px; border:1px solid rgba(124,58,237,0.4); border-radius:100px; font-size:0.72rem; letter-spacing:0.12em; text-transform:uppercase; color:#a78bfa; font-family:'DM Sans',sans-serif; }
        .btn-primary { display:inline-flex; align-items:center; gap:8px; background:#7c3aed; color:#fff; padding:14px 32px; border-radius:100px; font-size:0.85rem; font-weight:600; letter-spacing:0.06em; text-transform:uppercase; cursor:pointer; border:none; font-family:inherit; transition:background 0.2s,transform 0.2s,box-shadow 0.2s; }
        .btn-primary:hover { background:#6d28d9; transform:translateY(-2px); box-shadow:0 12px 40px rgba(124,58,237,0.35); }
        .btn-primary:disabled { cursor:not-allowed; } .btn-primary, .btn-outline, .nav-link { touch-action:manipulation; -webkit-tap-highlight-color:transparent; }
        .btn-outline { display:inline-flex; align-items:center; gap:8px; background:transparent; color:#f0f0f0; padding:14px 32px; border-radius:100px; font-size:0.85rem; font-weight:600; letter-spacing:0.06em; text-transform:uppercase; cursor:pointer; border:1px solid rgba(240,240,240,0.2); font-family:inherit; transition:border-color 0.2s,transform 0.2s; }
        .btn-outline:hover { border-color:#f0f0f0; transform:translateY(-2px); }
        .ticker-wrap { overflow:hidden; white-space:nowrap; border-top:1px solid rgba(255,255,255,0.07); border-bottom:1px solid rgba(255,255,255,0.07); padding:20px 0; }
        .ticker-inner { display:inline-block; animation:ticker 20s linear infinite; }
        .service-row { border-top:1px solid rgba(255,255,255,0.08); padding:36px 0; display:grid; grid-template-columns:60px 1fr 1fr; gap:24px; align-items:start; cursor:default; }
        .service-row:hover .snum { color:#7c3aed !important; }
        .ig-card { background:#0f0f0f; border:1px solid rgba(255,255,255,0.08); border-radius:20px; overflow:hidden; transition:transform 0.3s,border-color 0.3s; }
        .ig-card:hover { transform:translateY(-6px); border-color:rgba(124,58,237,0.4); }
        @keyframes ticker { from{transform:translateX(0)} to{transform:translateX(-50%)} }
        .marquee-track { display:flex; align-items:center; gap:0; width:max-content; animation:marquee 30s linear infinite; }
        .marquee-track:hover { animation-play-state:paused; }
        .marquee-item { flex-shrink:0; padding:0 32px; display:flex; align-items:center; justify-content:center; } @media(max-width:768px){.marquee-item{padding:0 20px;} .marquee-item img{width:100px!important;height:50px!important;}}
        @keyframes marquee { from{transform:translateX(0)} to{transform:translateX(-50%)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse { 0%,100%{opacity:0.3} 50%{opacity:1} }
        @media(max-width:768px){
          .service-row{grid-template-columns:36px 1fr;padding:24px 0;}
          .service-row .sdesc{display:none;}
          .about-grid{grid-template-columns:1fr!important;gap:40px!important;} .about-graphic{display:none!important;}
          .ig-grid{grid-template-columns:1fr!important;}
          .contact-grid{grid-template-columns:1fr!important;gap:40px!important;}
          .nav-desktop{display:none!important;}
          .nav-cta{display:none!important;}
          .hamburger{display:flex!important;}
          .stat-grid{grid-template-columns:1fr!important;} .stat-grid>*{border-left:1px solid rgba(255,255,255,0.1)!important;}
        }
        @media(min-width:769px){
          .hamburger{display:none!important;}
          .mobile-menu{display:none!important;}
        }
      `}</style>

      {/* Custom Cursor — desktop only */}
      {!isTouch && <div className="cursor-dot" style={{ left: cursor.x, top: cursor.y }} />}
      {!isTouch && <div className={`cursor-ring ${hovered ? "hovered" : ""}`} style={{ left: cursor.x, top: cursor.y }} />}

      {/* ─── NAV ─── */}
      <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, padding: "0 clamp(20px, 4vw, 48px)", height: 72, display: "flex", alignItems: "center", justifyContent: "space-between", background: scrolled ? "rgba(5,5,5,0.9)" : "transparent", backdropFilter: scrolled ? "blur(20px)" : "none", borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "none", transition: "all 0.4s ease" }}>
        <div style={{ fontWeight: 800, fontSize: "1.1rem", letterSpacing: "-0.02em", cursor: "pointer" }} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          Media Street<span style={{ color: "#7c3aed" }}>.</span>
        </div>
        {/* Desktop links */}
        <div className="nav-desktop" style={{ display: "flex", gap: 40 }}>
          {NAV_LINKS.map(l => (
            <button key={l.anchor} className="nav-link" onClick={() => scrollTo(l.anchor)}
              onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
              {l.label}
            </button>
          ))}
        </div>
        <button className="btn-primary nav-cta" style={{ padding: "10px 24px", fontSize: "0.78rem" }}
          onClick={() => scrollTo("contatti")}
          onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
          Prenota una Chiamata
        </button>
        {/* Hamburger */}
        <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)}
          style={{ flexDirection: "column", gap: 5, background: "none", border: "none", cursor: "pointer", padding: 4 }}>
          <span style={{ display: "block", width: 24, height: 2, background: "#f0f0f0", transition: "all 0.3s", transform: menuOpen ? "rotate(45deg) translate(5px,5px)" : "none" }} />
          <span style={{ display: "block", width: 24, height: 2, background: "#f0f0f0", transition: "all 0.3s", opacity: menuOpen ? 0 : 1 }} />
          <span style={{ display: "block", width: 24, height: 2, background: "#f0f0f0", transition: "all 0.3s", transform: menuOpen ? "rotate(-45deg) translate(5px,-5px)" : "none" }} />
        </button>
      </nav>

      {/* Mobile menu */}
      <div className="mobile-menu" style={{ position: "fixed", top: 72, left: 0, right: 0, zIndex: 999, background: "rgba(5,5,5,0.97)", backdropFilter: "blur(20px)", padding: "24px clamp(20px,4vw,48px) 32px", borderBottom: "1px solid rgba(255,255,255,0.06)", transform: menuOpen ? "translateY(0)" : "translateY(-100%)", opacity: menuOpen ? 1 : 0, transition: "all 0.3s ease", pointerEvents: menuOpen ? "all" : "none" }}>
        {NAV_LINKS.map(l => (
          <button key={l.anchor} className="nav-link" onClick={() => { scrollTo(l.anchor); setMenuOpen(false); }}
            style={{ display: "block", padding: "14px 0", fontSize: "1rem", borderBottom: "1px solid rgba(255,255,255,0.06)", width: "100%", textAlign: "left" }}>
            {l.label}
          </button>
        ))}
        <button className="btn-primary" style={{ marginTop: 24, width: "100%", justifyContent: "center" }}
          onClick={() => { scrollTo("contatti"); setMenuOpen(false); }}>
          Prenota una Chiamata
        </button>
      </div>

      {/* ─── HERO ─── */}
      <section style={{ height: "100vh", minHeight: 600, display: "flex", flexDirection: "column", justifyContent: "center", padding: "120px clamp(20px,4vw,48px) 80px", position: "relative", overflow: "hidden" }}>
        {/* LiquidEther background */}
        <LiquidEther
          colors={['#7c3aed', '#7c3aed', '#B19EEF']}
          mouseForce={20}
          cursorSize={100}
          isViscous={true}
          viscous={30}
          iterationsViscous={32}
          iterationsPoisson={32}
          resolution={0.5}
          isBounce={false}
          autoDemo={true}
          autoSpeed={0.5}
          autoIntensity={2.2}
          takeoverDuration={0.25}
          autoResumeDelay={3000}
          autoRampDuration={0.6}
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 0 }}
        />
        {/* Dark overlay so text stays readable */}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(5,5,5,0.55) 0%, rgba(5,5,5,0.75) 100%)", zIndex: 1 }} />
        <div style={{ maxWidth: 1200, margin: "0 auto", width: "100%", position: "relative", zIndex: 2 }}>
          <div style={{ marginBottom: 32, opacity: 0, animation: "fadeUp 0.8s ease 0.2s forwards" }}>
            <span className="pill">Agenzia di Digital Marketing</span>
          </div>
          <h1 style={{ fontSize: "clamp(2rem, 5.5vw, 7.5rem)", fontWeight: 800, lineHeight: 1.1, letterSpacing: "-0.03em", marginBottom: 52, opacity: 0, animation: "fadeUp 0.9s ease 0.4s forwards" }}>
            <span style={{ display: "block" }}>Non una semplice agenzia.</span>
            <span style={{ display: "block" }}>Il tuo intero</span>
            <span style={{ background: "#7c3aed", WebkitBoxDecorationBreak: "clone", boxDecorationBreak: "clone", padding: "4px 12px 8px 8px", display: "inline" }}>reparto marketing.</span>
          </h1>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", opacity: 0, animation: "fadeUp 0.9s ease 0.7s forwards" }}>
            <button className="btn-primary" onClick={() => scrollTo("servizi")}
              onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
              I Nostri Servizi ↗
            </button>
            <button className="btn-outline" onClick={() => scrollTo("contatti")}
              onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
              Contattaci
            </button>
          </div>
        </div>
        <div style={{ position: "absolute", bottom: 40, left: "50%", transform: "translateX(-50%)", opacity: 0.35, zIndex: 2 }}>
          <div style={{ width: 1, height: 60, background: "linear-gradient(to bottom, transparent, #7c3aed)", animation: "pulse 2s ease infinite" }} />
        </div>
      </section>

      {/* ─── TICKER ─── */}
      <div className="ticker-wrap">
        <div className="ticker-inner">
          {["Brand Strategy","Paid Media","SEO","Creatività","Analytics","Social","Brand Strategy","Paid Media","SEO","Creatività","Analytics","Social","Brand Strategy","Paid Media","SEO","Creatività","Analytics","Social "].map((t,i)=>(
            <span key={i} style={{ marginRight: 48, fontSize: "0.75rem", letterSpacing: "0.2em", textTransform: "uppercase", color: i%3===1 ? "#7c3aed" : "#444" }}>{t} ✦</span>
          ))}
        </div>
      </div>

      {/* ─── STATS ─── */}
      <section style={{ padding: "80px clamp(20px,4vw,48px)", maxWidth: 1200, margin: "0 auto" }}>
        <div className="stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)" }}>
          {STATS.map((s,i) => (
            <FadeUp key={s.label} delay={i*0.1}>
              <div style={{ padding: "48px 32px", borderLeft: i===0?"1px solid rgba(255,255,255,0.1)":"none", borderRight:"1px solid rgba(255,255,255,0.1)", borderTop:"1px solid rgba(255,255,255,0.1)", borderBottom:"1px solid rgba(255,255,255,0.1)" }}>
                <div style={{ fontSize: "clamp(2.5rem,4vw,4rem)", fontWeight: 800, letterSpacing: "-0.03em", color: "#fff", marginBottom: 8 }}>{s.value}</div>
                <div style={{ fontSize: "0.78rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "#666", fontFamily: "DM Sans, sans-serif" }}>{s.label}</div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ─── CLIENT LOGOS ─── */}
      <section style={{ padding: "64px 0", borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)", overflow: "hidden" }}>
        <p style={{ textAlign: "center", fontSize: "0.72rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "#3a3a3a", fontFamily: "DM Sans, sans-serif", marginBottom: 44 }}>
          Brand che hanno scelto Media Street
        </p>
        <div style={{ position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 120, background: "linear-gradient(to right, #050505, transparent)", zIndex: 2, pointerEvents: "none" }} />
          <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 120, background: "linear-gradient(to left, #050505, transparent)", zIndex: 2, pointerEvents: "none" }} />
          <div className="marquee-track">
            {[
              "/autoidea.png","/lesablier.png","/marcomorelli.png","/monster.png",
              "/studioequilibrium.png","/tempocasa.png","/wlake.png","/timerush.png","/veloce.png",
              "/autoidea.png","/lesablier.png","/marcomorelli.png","/monster.png",
              "/studioequilibrium.png","/tempocasa.png","/wlake.png","/timerush.png","/veloce.png",
            ].map((src, i) => (
              <div key={i} className="marquee-item">
                <img src={src} alt="" style={{ width: 160, height: 80, objectFit: "contain" }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── WORK / INSTAGRAM ─── */}
      <section id="lavori" style={{ padding: "80px clamp(20px,4vw,48px) 100px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <FadeUp>
            <div style={{ marginBottom: 64 }}>
              <span className="pill" style={{ marginBottom: 20, display: "inline-block" }}>Alcuni Casi Studio</span>
              <h2 style={{ fontSize: "clamp(2.5rem,5vw,5rem)", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1 }}>I numeri dei<br /><span style={{ color: "#7c3aed" }}>nostri clienti.</span></h2>
            </div>
          </FadeUp>
          <div className="ig-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24 }}>
            {[
              { handle: "@_autoidea_", tag: "Carrozzeria", img: "/ig-autoidea.png" },
              { handle: "@jennybmilano", tag: "Parrucchiere", img: "/ig-jenny.png" },
              { handle: "@street.legends.eventi", tag: "Organizzatore di eventi", img: "/ig-streetlegends.png", verified: true },
            ].map((c,i) => (
              <FadeUp key={i} delay={i*0.1}>
                <div className="ig-card"
                  onMouseEnter={e => { e.currentTarget.style.transform="translateY(-6px)"; e.currentTarget.style.borderColor="rgba(124,58,237,0.4)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.borderColor="rgba(255,255,255,0.08)"; }}>
                  <div style={{ position: "relative", overflow: "hidden" }}>
                    <img src={c.img} alt={c.handle} style={{ width: "100%", display: "block", objectFit: "cover", objectPosition: "top", maxHeight: 420 }} />
                    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 80, background: "linear-gradient(to top, #0f0f0f, transparent)" }} />
                  </div>
                  <div style={{ padding: "16px 18px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "0.88rem", display: "flex", alignItems: "center", gap: 6 }}>
                        {c.handle}{c.verified && <span style={{ color: "#4f9cf9" }}>✓</span>}
                      </div>
                      <div style={{ fontSize: "0.68rem", color: "#555", marginTop: 3, textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "DM Sans, sans-serif" }}>{c.tag}</div>
                    </div>
                    <div style={{ background: "rgba(124,58,237,0.15)", border: "1px solid rgba(124,58,237,0.25)", borderRadius: 100, padding: "5px 12px", fontSize: "0.65rem", color: "#a78bfa", whiteSpace: "nowrap" }}>Media Street ✦</div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SERVICES ─── */}
      <section id="servizi" style={{ padding: "80px clamp(20px,4vw,48px) 100px", background: "#0a0a0a" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <FadeUp>
            <div style={{ marginBottom: 72 }}>
              <span className="pill" style={{ marginBottom: 20, display: "inline-block" }}>Cosa Facciamo</span>
              <h2 style={{ fontSize: "clamp(2.5rem,5vw,5rem)", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1 }}>
                Tutto quello che ti serve.<br /><span style={{ color: "#7c3aed" }}>Sotto un solo tetto.</span>
              </h2>
            </div>
          </FadeUp>
          <div>
            {SERVICES.map((s,i) => (
              <FadeUp key={s.num} delay={i*0.07}>
                <div className="service-row">
                  <span className="snum" style={{ fontSize: "0.78rem", letterSpacing: "0.1em", color: "#444", transition: "color 0.3s", paddingTop: 4 }}>{s.num}</span>
                  <div style={{ fontSize: "1.5rem", fontWeight: 700, letterSpacing: "-0.02em" }}>{s.title}</div>
                  <div className="sdesc" style={{ fontSize: "0.88rem", color: "#555", fontFamily: "DM Sans, sans-serif", lineHeight: 1.7, maxWidth: 360 }}>{s.desc}</div>
                </div>
              </FadeUp>
            ))}
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }} />
          </div>
        </div>
      </section>

      {/* ─── ABOUT ─── */}
      <section id="chi-siamo" style={{ padding: "80px clamp(20px,4vw,48px)", maxWidth: 1200, margin: "0 auto" }}>
        <div className="about-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }}>
          <FadeUp>
            <div>
              <span className="pill" style={{ marginBottom: 24, display: "inline-block" }}>Chi Siamo</span>
              <h2 style={{ fontSize: "clamp(2rem,4vw,4rem)", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.05, marginBottom: 32 }}>
                Non siamo un fornitore.<br />Siamo <span style={{ color: "#7c3aed" }}>il tuo team.</span>
              </h2>
              <p style={{ fontSize: "1rem", color: "#666", fontFamily: "DM Sans, sans-serif", lineHeight: 1.8, marginBottom: 20 }}>
                Media Street è nata da marketer stanchi delle agenzie che riportavano solo metriche di vanità. La nostra ossessione è una sola: il fatturato. Ogni campagna, ogni creativo, ogni ottimizzazione è collegata a ciò che conta davvero.
              </p>
              <p style={{ fontSize: "1rem", color: "#666", fontFamily: "DM Sans, sans-serif", lineHeight: 1.8, marginBottom: 40 }}>
                Ci integriamo con il tuo team, lavoriamo ai tuoi ritmi e ci rendiamo responsabili di risultati concreti — non di impression.
              </p>
              <button className="btn-primary" onClick={() => scrollTo("contatti")}
                onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
                Scrivici ↗
              </button>
            </div>
          </FadeUp>
          <FadeUp delay={0.2}>
            <div className="about-graphic" style={{ position: "relative", aspectRatio: "1" }}>
              <div style={{ position: "absolute", inset: 0, borderRadius: 24, background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.07)", overflow: "hidden" }}>
                <svg width="100%" height="100%" style={{ position: "absolute", inset: 0, opacity: 0.15 }}>
                  <defs>
                    <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                      <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#7c3aed" strokeWidth="0.5"/>
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid)" />
                </svg>
                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "60%", height: "60%", background: "radial-gradient(circle, rgba(124,58,237,0.25) 0%, transparent 70%)" }} />
                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", textAlign: "center" }}>
                  <div style={{ fontSize: "2.2rem", fontWeight: 900, letterSpacing: "-0.04em", color: "#fff", lineHeight: 1 }}>Media</div>
                  <div style={{ fontSize: "2.2rem", fontWeight: 900, letterSpacing: "-0.04em", color: "#7c3aed", lineHeight: 1 }}>Street.</div>
                </div>
                <div style={{ position: "absolute", top: "12%", left: "8%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "10px 14px" }}>
                  <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#7c3aed" }}>+30</div>
                  <div style={{ fontSize: "0.58rem", color: "#555", textTransform: "uppercase", letterSpacing: "0.1em" }}>Clienti</div>
                </div>
                <div style={{ position: "absolute", top: "12%", right: "8%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "10px 14px" }}>
                  <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#fff" }}>9.1×</div>
                  <div style={{ fontSize: "0.58rem", color: "#555", textTransform: "uppercase", letterSpacing: "0.1em" }}>ROI Medio</div>
                </div>
                <div style={{ position: "absolute", bottom: "18%", left: "6%", background: "rgba(124,58,237,0.12)", border: "1px solid rgba(124,58,237,0.25)", borderRadius: 12, padding: "10px 14px" }}>
                  <div style={{ fontSize: "0.72rem", color: "#a78bfa", fontWeight: 600 }}>● Campagna attiva</div>
                  <div style={{ fontSize: "0.58rem", color: "#555", marginTop: 3 }}>Meta · Google · TikTok</div>
                </div>
                <div style={{ position: "absolute", bottom: "18%", right: "6%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "10px 14px" }}>
                  <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#fff" }}>5yr</div>
                  <div style={{ fontSize: "0.58rem", color: "#555", textTransform: "uppercase", letterSpacing: "0.1em" }}>Esperienza</div>
                </div>
                <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.1 }}>
                  <line x1="0" y1="100%" x2="100%" y2="0" stroke="#7c3aed" strokeWidth="1" strokeDasharray="6 6"/>
                </svg>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ─── CONTACT ─── */}
      <section id="contatti" style={{ padding: "80px clamp(20px,4vw,48px)", background: "#0a0a0a" }}>
        <div className="contact-grid" style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }}>
          <FadeUp>
            <div>
              <span className="pill" style={{ marginBottom: 28, display: "inline-block" }}>Contatti</span>
              <h2 style={{ fontSize: "clamp(2.8rem,5vw,5.5rem)", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 0.96, marginBottom: 28 }}>
                Costruiamo<br />qualcosa di<br /><span style={{ color: "#7c3aed" }}>straordinario.</span>
              </h2>
              <p style={{ fontSize: "0.97rem", color: "#555", fontFamily: "DM Sans, sans-serif", lineHeight: 1.8, marginBottom: 40 }}>
                Che tu stia scalando una startup o rinnovando un brand consolidato — siamo pronti a muoverci veloci e andare in profondità.
              </p>
              <a href="mailto:info@mediastreetagency.it" style={{ color: "#a78bfa", textDecoration: "none", fontSize: "0.88rem" }}>
                ✉ info@mediastreetagency.it
              </a>
            </div>
          </FadeUp>
          <FadeUp delay={0.15}>
            <ContactForm />
          </FadeUp>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer style={{ padding: "48px clamp(20px,4vw,48px)", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 24, marginBottom: 32 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: "1.1rem", letterSpacing: "-0.02em", marginBottom: 8 }}>
                Media Street<span style={{ color: "#7c3aed" }}>.</span>
              </div>
              <div style={{ fontSize: "0.75rem", color: "#444", fontFamily: "DM Sans, sans-serif", lineHeight: 1.7 }}>
                <div>Street Legends Eventi SRL</div>
                <div>P.IVA: 12763180960</div>
                <div>Corso Indipendenza 20, 20129 Milano (MI)</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
              {NAV_LINKS.map(l => (
                <button key={l.anchor} className="nav-link" onClick={() => scrollTo(l.anchor)}>{l.label}</button>
              ))}
            </div>
          </div>
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: 24, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div style={{ fontSize: "0.72rem", color: "#333", fontFamily: "DM Sans, sans-serif" }}>
              © 2026 Media Street Digital Agency. Tutti i diritti riservati.
            </div>
            <div style={{ display: "flex", gap: 24 }}>
              <a href="#" style={{ fontSize: "0.72rem", color: "#444", textDecoration: "none", fontFamily: "DM Sans, sans-serif" }}>Privacy Policy</a>
              <a href="#" style={{ fontSize: "0.72rem", color: "#444", textDecoration: "none", fontFamily: "DM Sans, sans-serif" }}>Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
